import { makeStyles } from '@material-ui/styles'
import moment from 'moment'
import { useState } from 'react'
import type { ServiceCertInfo } from 'api-client/resmgr.model'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Tooltip from 'core/elements/tooltip/Tooltip'
import colors from 'core/themes/base/colors'
import type Theme from 'core/themes/model'
import GlanceCertificateInfoModal from './GlanceCertificateInfoModal'
import { getCertificateExpiryStatus } from './helper'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    marginLeft: theme.spacing(0.75),
  },
  certButton: {
    display: 'inline-flex',
    alignItems: 'center',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    color: theme.palette.grey[600],
  },
  warningIcon: {
    color: colors.red[500],
  },
}))

interface Props {
  // Absent (undefined) hides this indicator entirely — PCD-9693 decision: no certificate info
  // for a host (e.g. feature not configured, or backend hasn't shipped it yet) means nothing
  // renders here, rather than a disabled placeholder.
  certInfo?: ServiceCertInfo
}

// PCD-9693: next to the Image Service status badge (Service Health tab's Image Service card,
// and the Host Health tab), shows a clickable certificate icon that opens read-only certificate
// details, plus a red warning triangle when the Image library certificate is nearing expiry or has expired.
const GlanceCertificateStatusIndicator = ({ certInfo }: Props) => {
  const classes = useStyles({})
  const [open, setOpen] = useState(false)

  if (!certInfo) {
    return null
  }

  const status = getCertificateExpiryStatus(certInfo.expiry_date)
  const expiryDate = moment.unix(certInfo.expiry_date).format('MMM D, YYYY')

  return (
    <span className={classes.container}>
      {status !== 'healthy' && (
        <Tooltip
          message={
            status === 'expired'
              ? `Image library certificate expired on ${expiryDate}. Click to view details`
              : `Image library certificate is expiring on ${expiryDate}. Click to view details`
          }
        >
          <button
            type="button"
            className={classes.certButton}
            aria-label="View certificate details"
            onClick={() => setOpen(true)}
          >
            <FontAwesomeIcon className={classes.warningIcon} solid>
              triangle-exclamation
            </FontAwesomeIcon>
          </button>
        </Tooltip>
      )}
      <GlanceCertificateInfoModal open={open} onClose={() => setOpen(false)} certInfo={certInfo} />
    </span>
  )
}

export default GlanceCertificateStatusIndicator
